import { Span, Tags as OTTags } from 'opentracing';
import * as Tags from './tags';
import {
  WEBSITE_ORIGIN_POD_TAG,
  getWebsiteOriginPod,
} from '../website-origin-pod';

export function extractWebgateInfo(req: Request) {
  const wgRegex = /wg\.spotify\.com\/([^/?]+)/;
  const match = wgRegex.exec(req.url);
  if (!match || !match[1]) return null;
  return { service: match[1] };
}

export function addWebgateServiceNameTag(span: Span, req: Request) {
  const info = extractWebgateInfo(req);
  if (!info) return;
  span.setTag(Tags.WEBGATE_SERVICE, info.service);
}

export function extractHostname(req: Request) {
  try {
    const parsed = new URL(req.url);
    return parsed.hostname;
  } catch {
    // Can happen if the url is empty or invalid
    return null;
  }
}

export function addServiceInfoTags(
  span: Span,
  serviceVersion: string,
  serviceHash: string,
) {
  span.addTags({
    [Tags.SERVICE_VERSION]: serviceVersion,
    [Tags.SERVICE_HASH]: serviceHash,
  });
}

export function addWebsiteOriginPodTag(span: Span) {
  span.setTag(WEBSITE_ORIGIN_POD_TAG, getWebsiteOriginPod());
}

export function addDefaultRequestTags(span: Span, req: Request) {
  const parsed = new URL(req.url);

  // See https://docs.lightstep.com/docs/use-tags-to-find-issues-fast#useful-tags
  // and https://github.com/opentracing/specification/blob/master/semantic_conventions.md
  span.addTags({
    [OTTags.HTTP_METHOD]: req.method,
    [OTTags.HTTP_URL]: req.url,
    [Tags.HTTP_MODE]: req.mode,
    [Tags.HTTP_HASH]: parsed.hash,
    [Tags.HTTP_HREF]: parsed.href,
    [Tags.HTTP_PROTOCOL]: parsed.protocol,
    [Tags.HTTP_ORIGIN]: parsed.origin,
    [Tags.HTTP_HOST]: parsed.host,
    [Tags.HTTP_HOSTNAME]: parsed.hostname,
    [Tags.HTTP_PORT]: parsed.port,
    [Tags.HTTP_PATHNAME]: parsed.pathname,
    [Tags.HTTP_SEARCH]: parsed.search,
    [Tags.OPERATION_KIND]: 'fetch',
  });
}

export function addDefaultResponseTags(span: Span, res: Response) {
  span.addTags({
    [Tags.HTTP_RES_REDIRECTED]: res.redirected,
    [Tags.HTTP_RES_TYPE]: res.type,
    [Tags.HTTP_RES_URL]: res.url,
    [Tags.HTTP_RES_OK]: res.ok,
  });

  span.setTag(OTTags.HTTP_STATUS_CODE, res.status);
  const statusCodeGroup = `${Math.floor(res.status / 100)}XX`;
  if (statusCodeGroup) {
    span.setTag(Tags.HTTP_STATUS_CODE_GROUP, statusCodeGroup);
  }

  if (res.status >= 500) {
    span.setTag(OTTags.ERROR, true);
    span.log({ 'error.status_text': res.statusText });
  }
}

export function addDefaultNavigatorTags(span: Span, navigator: Navigator) {
  span.addTags({
    [Tags.NAVIGATOR_ONLINE]: navigator.onLine,
    [Tags.NAVIGATOR_USER_AGENT]: true,
  });
  // The majority of user-agents are ~80 characters, longer ones are 200, maybe
  // even 4K. We tag that there is a UA attached to this trace, and then log the
  // UA to avoid blowing up the Lightstep UI.
  span.log({ [Tags.NAVIGATOR_USER_AGENT]: navigator.userAgent });
}
